<template>
  <div>
    <field-label
      :label="reportTemplates.length > 0 ? 'Modelo de impressão' : ''"
      titleClass="text-body-1"
      titleColorClass="black--text text--lighten-1"
      class="my-4"
    >
      <div class="d-flex gap-8 align-end flex-wrap">
        <select-printing-template v-model="selectedTemplate" type="report" style="max-width: 400px" />
        <v-btn @click="resetReport" color="primary" :disabled="isFinishedAttendance">Gerar novamente</v-btn>
        <v-btn color="primary" :disabled="!report || report.length === 0" :loading="printing" @click="printPdf">
          Imprimir
        </v-btn>
      </div>
    </field-label>

    <field-label label="Relatório" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="report" :rows="20" :disabled="isFinishedAttendance" />
    </field-label>
    <field-label label="Referências" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="references" :disabled="isFinishedAttendance" />
    </field-label>

    <attendance-select-dialog ref="attendanceSelectDialog" @selectAttendance="onSelectAttendance" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import AttendanceSelectDialog from '@/components/AttendanceForm/AttendanceSelectDialog.vue'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
const {
  mapGetters: attendanceGettersMap,
  mapActions: attendanceActionsMap,
  mapState: attendanceStateMap,
} = createNamespacedHelpers('attendance')

import psoriasisSummaryMixin from '@/components/AttendanceForm/psoriasis/mixins/psoriasisSummaryMixin'

import TextArea from '@/components/TextArea.vue'
import { STATUS_FINISHED } from '@/constants/attendance'
import { COMORBIDITIES_OPTIONS } from '../../general/constants/personalHistory'
import SelectPrintingTemplate from '@/components/PrintingTemplate/SelectPrintingTemplate.vue'

export default {
  mixins: [psoriasisSummaryMixin],
  extends: BasePhase,
  components: {
    FieldLabel,
    TextArea,
    AttendanceSelectDialog,
    SelectPrintingTemplate,
  },
  computed: {
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData', 'attendance']),
    ...attendanceGettersMap(['attendance', 'getPhaseDataByKey']),
    previousTuberculosisText() {
      let previousTuberculosis = this.investigationPhaseData?.profile?.tuberculose?.value != 'NÃO' ?? null
      if (previousTuberculosis === null) {
        return 'Histórico de tuberculose prévia não respondido'
      } else if (previousTuberculosis) {
        return 'Tem histórico de tuberculose prévia'
      } else {
        return 'Não tem histórico de tuberculose prévia'
      }
    },
    tuberculosisRiskText() {
      let tuberculosisRisk = this.investigationPhaseData?.profile?.tuberculose_latente?.value ?? null
      if (tuberculosisRisk === null) {
        return 'Risco para contato com tuberculose não respondido'
      } else if (tuberculosisRisk) {
        return 'Tem alto risco para contato com tuberculose'
      } else {
        return 'Não tem alto risco para contato com tuberculose'
      }
    },
    investigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_pessoal')
    },
    psychiatricPhaseData() {
      return this.getPhaseDataByKey('triagem_psiquiatrica')
    },
    medicineHistory() {
      return Object.values(this.getPhaseDataByKey('dados_especificos')?.medicines || {}) || []
    },
    hasFirstPrescription() {
      let treatments = this.getPhaseDataByKey('medicamentos')?.selectedTreatments || []
      return treatments.some((treatment) => treatment.selectedDosage.key === 'firstPrescription')
    },
    hasTreatment() {
      const treatments = this.getPhaseDataByKey('medicamentos')?.selectedTreatments || []
      return treatments.length > 0
    },
    currentMedicinesText() {
      let currentMedicines = this.medicineHistory.filter((m) => m.periods.some((p) => !p.end))
      if (currentMedicines.length === 0) {
        return ''
      }
      return (
        'Está em uso atual de: ' +
        currentMedicines
          .map(
            (m) =>
              `${m.medicine.name.firstLetterToLowercase()}${
                ' (desde ' + this.$util.toFormattedDate(m.periods.find((p) => !p.end).start) + ')'
              }`,
          )
          .join(', ')
      )
    },
    previousMedicineText() {
      let previousMedicines = this.medicineHistory.filter(
        (m) => m.periods.length === 0 || m.periods.some((p) => !!p.end),
      )

      if (previousMedicines.length === 0) {
        return 'nenhum'
      }

      return previousMedicines
        .map((m) => {
          let periods =
            m.periods.length === 0
              ? ''
              : m.periods
                  .filter((p) => !!p.end)
                  .map((p) => `de ${this.$util.toFormattedDate(p.start)} a ${this.$util.toFormattedDate(p.end)}`)
                  .join(', ')

          return `${m.medicine.name.firstLetterToLowercase()} ${periods.length > 0 ? `(${periods})` : ''}`
        })
        .join(', ')
    },
    prescriptedMedicinesText() {
      if (!this.getPhaseDataByKey('medicamentos')?.selectedTreatments) {
        return ''
      }
      return this.getPhaseDataByKey('medicamentos')
        .selectedTreatments.map((t) => t.medicine.name)
        .join(', ')
    },
    previousAttendanceMedicinesText() {
      if (!!this.oldAttendance) {
        return this.oldAttendance.data.medicamentos
          ? this.oldAttendance.data.medicamentos.selectedTreatments?.map((t) => t.medicine?.name).join(', ')
          : null
      } else if (!this.oldAttendance.this.oldAttendance.data.medicamentos.selectedTreatments) {
        return 'nenhum'
      }
      return ''
    },
    comorbiditiesText() {
      if (
        !this.investigationPhaseData ||
        this.investigationPhaseData.noDisease ||
        !this.investigationPhaseData.diseases ||
        this.investigationPhaseData.diseases.length === 0
      ) {
        return null
      }

      return this.investigationPhaseData.diseases
        .map(
          (d) =>
            COMORBIDITIES_OPTIONS.find((o) => o.key === d)?.name +
            (d === 'outras_comorbidades' ? ` (${this.investigationPhaseData.otherDisease})` : ''),
        )
        .join(', ')
    },
    jointInvestigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_articular')
    },
    psoriaticArthritisText() {
      if (!this.jointInvestigationPhaseData) {
        return ''
      }
      const value = this.jointInvestigationPhaseData?.diagnostico_artrite_psoriatica?.value

      if (value === 'NAO') {
        return 'Sem artrite psoriática.'
      } else if (value === 'SIM') {
        return 'Com artrite psoriática.'
      } else if (value === 'NÃO_SEI' && (parseInt(this.computedCaspar) > 2 || parseInt(this.computedPest) > 2)) {
        return 'Com suspeita de artrite psoriática.'
      }
      return ''
    },
    defaultReport() {
      return this.getReportText()
    },
    maintanceReport() {
      return this.getMaintanceReportText()
    },
    defaultReferences() {
      const treatments = this.getPhaseDataByKey('medicamentos')?.selectedTreatments || []
      if (!treatments.length > 0) {
        return ''
      }
      return treatments.map((t) => t.references.map((r) => r.reference).join('\n')).join('\n')
    },
    reportTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    printReportUrl() {
      return `${this.$util.apiURL()}/attendance/report/print/${this.attendance.id}/${this.selectedReportTemplateId}`
    },
    isFinishedAttendance() {
      return this.attendance?.status === STATUS_FINISHED.value
    },
    hasValidOldAttendance() {
      if (!!this.oldAttendances) {
        return (
          this.oldAttendances.items.filter(
            (attendance) => attendance.endedAt != null && attendance.disease.name === 'Psoríase',
          ).length > 0
        )
      } else return false
    },
  },
  data() {
    return {
      report: null,
      references: null,
      templates: [],
      selectedTemplate: null,
      printing: false,
      selectedReportTemplateId: null,
      oldAttendance: null,
      oldAttendances: null,
    }
  },
  async mounted() {
    await this.loadData()
    this.oldAttendances = await this.$api.getPatientAttendances(this.attendance.patient.id)
  },
  methods: {
    ...attendanceActionsMap([
      'setAttendanceMedicalReport',
      'setAttendanceMedicalReportReferences',
      'startServerSyncing',
      'endServerSyncing',
    ]),
    async printPdf() {
      await this.saveReport()
      try {
        this.printing = true
        const blob = await this.$api.printingTemplate.print({
          templateId: this.selectedTemplate.id,
          patientId: this.attendance.patient.id,
          text: this.report,
          extraText: this.references,
        })
        this.$util.downloadBlob(blob, 'relatório.pdf')
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.printing = false
      }
    },
    getMaintanceReportText() {
      const name = this.attendance.patient.socialName || this.attendance.patient.name
      const age = this.$util.toAge(this.attendance.patient.birthday)

      const diagnosisTimeInYears = this.$util.toAgeNumber(
        this.oldAttendance?.data?.dados_especificos?.ano_diagnostico_psoriase,
      )

      const thisYear = new Date().getFullYear()

      let diagnosisTimeString = ''

      if (!diagnosisTimeInYears) {
        diagnosisTimeString = 'com tempo indefinido'
      } else {
        diagnosisTimeString =
          diagnosisTimeInYears === 0
            ? `desde ${thisYear}`
            : `há ${diagnosisTimeInYears > 1 ? diagnosisTimeInYears + ' anos' : '1 ano'}`
      }

      const currentPasi = this.computedPasi === '?' ? 'indefinido' : this.computedPasi
      const currentBsa = this.computedBsa === '?' ? 'indefinido' : this.computedBsa
      const currentDlqi = this.computedBsa === '?' ? 'indefinido' : this.computedDlqi

      const oldPasi =
        this.oldAttendance?.summary?.pasi?.value === '?' ? 'indefinido' : this.oldAttendance.summary.pasi.value
      const oldBsa =
        this.oldAttendance?.summary?.bsa?.value === '?' ? 'indefinido' : this.oldAttendance.summary.bsa.value
      const oldDlqi =
        this.oldAttendance?.summary?.dlqi?.value === '?' ? 'indefinido' : this.oldAttendance.summary.dlqi.value

      const paragraphs = [
        `${name}, ${age}, apresenta histórico de lesões eritematosas e descamativas compatíveis com diagnóstico de Psoríase CID L40.0 ${diagnosisTimeString}. ${this.psoriaticArthritisText} `,
      ]

      if (this.previousAttendanceMedicinesText?.length > 0) {
        paragraphs.push(
          `\nRealizou tratamento prévio da patologia com ${this.previousAttendanceMedicinesText} por tempo e com doses adequadas entretanto sem controle efetivo da doença. `,
        )
      }

      paragraphs.push(
        `\nAntes do tratamento atual, apresentava os seguintes critérios de gravidade: PASI (Psoriasis Area and Severity Index) ${oldPasi}, BSA (Body Surface Area) ${oldBsa}, DLQI (Dermatology Life Quality Index) ${oldDlqi}. `,
      )

      if (this.currentMedicinesText.length > 0) {
        paragraphs.push(`${this.currentMedicinesText}`)
      }

      paragraphs.push(`Apresentando atualmente PASI: ${currentPasi}, BSA: ${currentBsa}, DLQI: ${currentDlqi}.`)

      paragraphs.push(
        'Como vem apresentando boa resposta com a terapia proposta, tendo atingido os critérios de melhora PASI, BSA e DLQI estabelecidos para a doença conforme o Consenso Brasileiro de Psoríase e PCDT e não apresenta eventos adversos ou outras contra-indicações, solicito a liberação do medicamento conforme receita em anexo.',
      )

      paragraphs.push(`\nFico à disposição para eventuais esclarecimentos.`)

      paragraphs.push(`\nAtenciosamente,`)

      paragraphs.push(
        `\nSegue link para o documento Protocolo Clínico e Diretrizes de Tratamento (PCDT) para psoríase do Ministério da Saúde de 2019 onde indica que medicamentos tradicionais ou imunobiológicos podem ser dispensados para pacientes com doença moderada a grave (PASI, BSA ou DLQI maior ou igual a 10) ou em áreas especiais com falha ao tratamento por mais de 3 meses. http://conitec.gov.br/images/Consultas/Relatorios/2021/20210615_Relatorio_Psoriase_CP_52.pdf`,
      )

      paragraphs.push(
        `\nSegue link para o ROL de medicamentos da ANS para "Psoríase" que prevê a cobertura obrigatória de terapia imunobiológica para pacientes com psoríase em placas moderada a grave ou de áreas especiais com alto impacto na qualidade de vida e resistente aos tratamentos convencionais. http://www.in.gov.br/web/dou/-/resolucao-normativa-ans-n-536-de-2-de-maio-de-2022-398111061`,
      )
      return paragraphs.join('\n')
    },
    getReportText() {
      const age = this.$util.toAge(this.attendance.patient.birthday)

      const pasi = this.computedPasi === '?' ? 'indefinido' : this.computedPasi
      const bsa = this.computedBsa === '?' ? 'indefinido' : this.computedBsa
      const dlqi = this.computedDlqi === '?' ? 'indefinido' : this.computedDlqi

      const comorbidity =
        !this.investigationPhaseData ||
        this.investigationPhaseData?.noDisease ||
        this.investigationPhaseData?.diseases?.length === 0
          ? 'não apresenta comorbidades'
          : `apresenta as seguintes comorbidades: ${this.comorbiditiesText}`

      const obese =
        this.$util.calcImc(this.attendance.patient.currentWeightKg, this.attendance.patient.currentHeightCm) >= 25

      const anxiety = this.anxietyScore >= 2 ? 'Suspeita de transtorno de ansiedade' : null
      const depression = this.depressionScore >= 2 ? 'Suspeita de transtorno depressivo' : null
      const topicTreatmentFailure =
        this.getPhaseDataByKey('areas_afetadas')?.medicamentos_previos_mais_de_3_meses === 'SIM_SEM_MELHORIA'

      const name = this.attendance.patient.socialName || this.attendance.patient.name
      const paragraphs = [
        `${name}, ${age}, apresenta histórico de lesões eritematosas e descamativas compatíveis com diagnóstico de psoríase ${this.diseaseLevelText}${this.diagnosisTimeText} ${this.affectedAreasText}, com os seguintes critérios de gravidade: PASI (Psoriasis Area and Severity Index) ${pasi}, BSA (Body Surface Area) ${bsa}, DLQI (Dermatology Life Quality Index) ${dlqi}. (CID L40.0).`,
        this.psoriaticArthritisText,
      ]

      if (this.previousMedicineText.length > 0) {
        paragraphs.push(
          `Realizou tratamento prévio da patologia com ${this.previousMedicineText} por tempo e com doses adequados. Entretanto, não obteve melhora satisfatória da manifestação das lesões ou de sua qualidade de vida.`,
        )
      }
      if (this.previousMedicineText.length > 0) {
        paragraphs.push(this.currentMedicinesText)
      }
      if (topicTreatmentFailure) {
        paragraphs.push(
          'Apesar do uso de tratamentos tópicos como corticosteróides, ácido salicílico, calcipotriol associados a emolientes por mais de 3 meses contínuos, as lesões de psoríase de áreas especiais persistiram (falha terapêutica).',
        )
      }

      paragraphs.push(
        `\nA psoríase é uma doença inflamatória crônica associada a múltiplas comorbidades. Pacientes com essa patologia tem maior risco de desenvolver síndrome metabólica, doenças cardiovasculares, doença hepática não gordurosa, doença inflamatória intestinal, artrite psoriásica e doenças psiquiátricas como transtornos ansiosos e depressivos incluindo maiores taxas de ideação suicida. Mais especificamente, pacientes com doença severa ou que desenvolvem psoríase em idade precoce têm maior risco de desenvolver comorbidades cardiometabólicas. Ao mesmo tempo, pacientes com quadros de psoríase grave possuem um risco significativamente maior de infarto de miocárdio, acidente vascular cerebral e mortalidade cardiovascular que a população geral. Por isso, é essencial instituir um tratamento efetivo e abrangente para controle da doença e prevenção dessas complicações.\n`,
      )

      paragraphs.push(
        `O paciente acima apresenta sintomas de ansiedade associados a presença de lesões em áreas visíveis da pele que trazem prejuízos e desconforto no ambiente de trabalho, social e pessoal. Estas lesões estão eventualmente associadas à prurido, ficam com escamas que sujam a roupa ou o ambiente, causam preocupação em relação ao vestuário e grande desgaste físico e emocional associado aos cuidados diários com a pele, apesar da ineficácia dos tratamentos até o momento.\n`,
      )

      paragraphs.push(`Além disso, ${comorbidity}.`)

      if (obese) {
        paragraphs.push('Com obesidade.')
      }

      if (this.investigationPhaseData?.medicine) {
        paragraphs.push('Uso contínuo de: ' + this.investigationPhaseData.medicine)
      }

      paragraphs.push(`${this.previousTuberculosisText}.`)
      paragraphs.push(`${this.tuberculosisRiskText}.`)

      paragraphs.push(
        `\nDiversos estudos clínicos, revisões, consensos e diretrizes nacionais e internacionais orientam o tratamento da psoríase baseada na classificação de gravidade da doença conforme PASI, BSA e DLQI (qualquer um desses maior ou igual a 10 é considerado moderado a grave), além de considerar a presença ou não de artrite psoriásica e o acometimento de áreas especiais como face, couro cabeludo, unhas, palmo-plantar, genital e invertida.`,
      )
      paragraphs.push(
        `\nO Protocolo Clínico e Diretrizes de Tratamento para psoríase do Ministério da Saúde de 2019 (PCDT) orienta que são candidatos aos tratamentos sistêmicos com medicamentos tradicionais ou imunobiológicos os seguintes pacientes:`,
      )
      paragraphs.push(`     - com psoríase em placas moderada a grave (PASI, BSA ou DLQI maior ou igual a 10);`)
      paragraphs.push(
        `     - com lesões em áreas especiais como face, couro cabeludo, unhas, palmas, plantas, flexuras resistentes ao tratamento tópico tradicional por pelo menos 3 meses;`,
      )
      paragraphs.push(`     - com psoríase vulgar leve persistente apesar de tratamento localizado.`)

      paragraphs.push(
        `\nAlém disso, medicamentos imunobiológicos estão inclusos no rol de medicamentos da ANS para a patologia psoríase sendo de cobertura obrigatória pelas operadoras de saúde para pacientes com psoríase vulgar moderada a grave ou de áreas especiais resistente ao tratamento convencional e com alto impacto na qualidade de vida (DLQI> ou = 10).`,
      )

      paragraphs.push(`\nSegue abaixo link para documento da ANS:`)
      paragraphs.push(`https://www.in.gov.br/web/dou/-/resolucao-normativa-ans-n-536-de-2-de-maio-de-2022-398111061`)

      if (this.prescriptedMedicinesText.length > 0) {
        paragraphs.push(`\nPor isso, considerando o subtipo de doença, sua gravidade, o perfil e histórico de doença do paciente, solicito tratamento com ${this.prescriptedMedicinesText} para uso conforme receita em anexo.
Esse medicamento tem alta eficácia no subtipo de psoríase desse paciente, com resposta sustentada a longo prazo, com perfil de segurança adequado comprovados em estudos (segue referência bibliográfica abaixo
que contém tais informações). Com esse tratamento, o paciente poderá ter remissão parcial ou eventualmente total das lesões de pele e seus sintomas.`)
      }

      paragraphs.push(
        `\nRessalto que o objetivo em relação ao tratamento da psoríase aqui solicitado é atingir melhora de 90% do PASI inicial com PASI absoluto final abaixo de 3 com recuperação da qualidade de vida e bem estar psicossocial do paciente, atingindo DLQI 0 ou 1, ou seja, nenhum impacto da doença na qualidade de vida do paciente. Essas são as metas terapêuticas definidas para o tratamento da psoríase pela Sociedade Brasileira de Dermatologia no "Consenso Brasileiro de Psoríase de 2020".`,
      )

      paragraphs.push(
        `\nComprometo-me a realizar seguimento do caso em consultas regulares para orientar o paciente quanto ao seguimento correto do tratamento, monitorar sua adesão às medidas propostas e observar os efeitos positivos do medicamento assim como tratar com rapidez e responsabilidade os possíveis efeitos adversos associados à terapêutica proposta.`,
      )

      paragraphs.push(`\nFico à disposição para eventuais esclarecimentos.`)

      paragraphs.push(`\nAtenciosamente,`)

      return paragraphs.join('\n')
    },
    async loadData() {
      this.templates = (await this.$api.getTemplates()).items

      if (this.reportTemplates.length > 0) {
        this.selectedReportTemplateId = this.reportTemplates[0].id
      }
    },

    fillDefaultReportAndReferences() {
      this.report = this.defaultReport
      this.references = this.defaultReferences
    },
    async resetReport() {
      if (!this.hasFirstPrescription && this.hasValidOldAttendance && this.hasTreatment) {
        if (
          await this.$root.confirm(
            'Deseja gerar um relatório comparativo? Ao clicar em cancelar um relatório padrão será gerado.',
          )
        ) {
          this.openSelectDialog()
        } else {
          this.fillDefaultReportAndReferences()
        }
      } else {
        if (await this.$root.confirm('Deseja gerar o relatório novamente? As alterações serão perdidas!')) {
          this.fillDefaultReportAndReferences()
        }
      }
    },
    getDefaultData() {
      return null
    },

    async saveMedicalDocument() {
      const data = {
        report: this.report,
        references: this.references,
      }
      return this.$api.medicalDocument.create({
        patientId: this.attendance.patient.id,
        kind: 'report',
        data,
        templateId: this.selectedTemplate.id,
        attendanceId: this.attendance.id,
      })
    },
    async saveReport() {
      try {
        await this.saveMedicalDocument()
      } catch (e) {
        console.error(e)
      }

      if (this.isServerSyncedData || this.isSavingData) {
        return
      }

      try {
        await this.startServerSyncing()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
        await this.endServerSyncing(true)
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }
    },
    openSelectDialog() {
      this.$refs.attendanceSelectDialog.openDialog(this.attendance.patient.id)
    },
    async onSelectAttendance(attendance) {
      this.oldAttendance = await this.$api.getAttendance(attendance.id)
      this.report = this.maintanceReport
      this.references = this.defaultReferences
      this.$refs.attendanceSelectDialog.closeDialog()
    },
  },

  watch: {
    active: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.references = this.attendance.references
          this.report = this.attendance.report

          if ((!this.report || this.report.length === 0) && (!this.references || this.references.length === 0)) {
            if (
              await this.$root.confirm(
                `O relatório médico está vazio. Deseja preencher com o texto padrão para psoríase?`,
              )
            ) {
              if (!this.hasFirstPrescription && this.hasValidOldAttendance) {
                this.openSelectDialog()
              } else {
                this.fillDefaultReportAndReferences()
              }
            }
          }
        }
      },
    },
    report(newVal) {
      this.setAttendanceMedicalReport(newVal)
    },
    references(newVal) {
      this.setAttendanceMedicalReportReferences(newVal)
    },
  },
}
</script>

<style scoped>
* :deep(textarea) {
  padding: 10px 20px;
}
</style>
